import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useAddTestQuestion from "./useAddTestQuestion";
import { CancelButton, FormButton } from "../../../../components/Botton";

// Define field types
type BaseField = {
  label: string;
  name: string;
  type: "text" | "textarea" | "select" | "checkbox";
  disabled?: boolean;
};

type SelectField = BaseField & {
  type: "select" ;
  options: any[];
};

type Field = BaseField | SelectField;

const AddTestQuestion = () => {
  const {
    loading,
    courses,
    languages,
    subjectName,
    standard,
    formData,
    handleChange,
    deleteQuestion,
    handleSubmit,
    addQuestion,
    handleCancel,
  } = useAddTestQuestion();

  const {
    control,
    register,
    handleSubmit: useFormSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  // State for filtered data
  const [filteredCourses, setFilteredCourses] = useState<any[]>([]);
  const [filteredsubjects, setFilteredsubjects] = useState<any[]>([]);

  // Watch for changes to filter options based on selected subject and standard
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedStandard, setSelectedStandard] = useState('');

  useEffect(() => {
    // Filter courses based on selected standard
    if (selectedStandard) {
      const filtered = courses.filter(course => course.standard === selectedStandard);
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses([]);
    }

    // Filter subjects based on both standard and course
    if (selectedStandard && selectedCourse) {
      const filtered = subjectName.filter(subject => 
        subject.standard === selectedStandard && 
        subject.courseName === selectedCourse
      );
      setFilteredsubjects(filtered);
    } else {
      setFilteredsubjects([]);
    }
  }, [selectedStandard, selectedCourse, courses, subjectName]);

  const fields: Field[] = [
    { 
      label: "Question Text", 
      name: "questionText", 
      type: "textarea" 
    },
    { 
      label: "Description", 
      name: "description", 
      type: "textarea" 
    },
    { 
      label: "Difficulty Level", 
      name: "level", 
      type: "select", 
      options: [{ level: "Easy" }, { level: "Medium" }, { level: "Hard" }] 
    },
    { 
      label: "Education", 
      name: "standard", 
      type: "select", 
      options: standard 
    },
    
    { 
      label: "Stream", 
      name: "courseName", 
      type: "select", 
      options: filteredCourses,
      disabled: !selectedStandard 
    },
    { 
      label: "Subject", 
      name: "subject", 
      type: "select", 
      options: filteredsubjects,
      disabled: !selectedStandard || !selectedCourse 
    },
    { 
      label: "Language Name", 
      name: "languageName", 
      type: "select", 
      options: languages 
    },
    { 
      label: "Also add as live MCQ ?", 
      name: "isLiveMcq", 
      type: "checkbox",
      // options: [{ isLiveMcq: "Yes" }, { isLiveMcq: "No" }] 
    },
  ];

  // Handle standard change
  const handleStandardChange = (e: React.ChangeEvent<HTMLSelectElement>, questionIndex: number) => {
    const value = e.target.value;
    setSelectedStandard(value);
    setSelectedCourse(''); // Reset course when standard changes
    setFilteredsubjects([]); // Reset subjects when standard changes
    handleChange(e, questionIndex);
    setValue(`questions.${questionIndex}.${e.target.name}`, value);
    // Clear dependent fields
    setValue(`questions.${questionIndex}.courseName`, '');
    setValue(`questions.${questionIndex}.subject`, '');
  };

  // Handle course change
  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>, questionIndex: number) => {
    const value = e.target.value;
    setSelectedCourse(value);
    handleChange(e, questionIndex);
    setValue(`questions.${questionIndex}.${e.target.name}`, value);
    // Clear subject when course changes
    setValue(`questions.${questionIndex}.subject`, '');
  };

  return (
    <div className="flex flex-col items-center w-full h-screen p-8">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
        <div className="border-b-2 w-[100%]">
          <h1 className="text-3xl mb-2 text-gray-700 ml-5">Add Test Question</h1>
        </div>
        <form onSubmit={useFormSubmit(handleSubmit)} className="space-y-6">
          {formData.questions.map((question: any, questionIndex: number) => (
            <fieldset key={questionIndex} className="border p-4 rounded mb-4 bg-white">
              <legend className="text-xl mb-4 font-semibold text-gray-700">
                Question {questionIndex + 1}
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fields.map((field) => (
                  <div key={field.name}>
                    <label
                      className="block text-gray-700 text-base font-semibold mb-2"
                      htmlFor={`${field.name}-${questionIndex}`}
                    >
                      {field.label}:
                    </label>
                    {field.type === "select" ? (
                      <select
                        id={`${field.name}-${questionIndex}`}
                        {...register(`questions.${questionIndex}.${field.name}`, { required: true })}
                        name={field.name}
                        value={question[field.name] || ""}
                        onChange={(e) => {
                          if (field.name === "standard") {
                            handleStandardChange(e, questionIndex);
                          } else if (field.name === "courseName") {
                            handleCourseChange(e, questionIndex);
                          } else {
                            handleChange(e, questionIndex);
                            setValue(`questions.${questionIndex}.${field.name}`, e.target.value);
                          }
                        }}
                        className={`appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline ${
                          field.disabled ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        disabled={field.disabled}
                      >
                        <option value="">Select {field.label}</option>
                        {field.type === 'select' && (field as SelectField).options?.map((option: any) => (
                          <option key={option?._id} value={option[field.name]}>
                            {option[field.name]}
                          </option>
                        ))}
                      </select>
                    ) : field.type === "textarea" ? (
                      <textarea
                        id={`${field.name}-${questionIndex}`}
                        {...register(`questions.${questionIndex}.${field.name}`, { required: true })}
                        name={field.name}
                        value={question[field.name] || ""}
                        onChange={(e) => {
                          handleChange(e, questionIndex);
                            setValue(`questions.${questionIndex}.${field.name}`, e.target.value);
                          }}
                          className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                          />
                        ) : field.type === "checkbox" ? (
                          <input
                            type="checkbox"
                            id={`${field.name}-${questionIndex}`}
                            {...register(`questions.${questionIndex}.${field.name}`)}
                            name={field.name}
                            checked={question[field.name] || false}
                            onChange={(e) => {
                              handleChange(e, questionIndex);
                              setValue(`questions.${questionIndex}.${field.name}`, !(question[field.name] ?? false));
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                        ) : (
                          <input
                          id={`${field.name}-${questionIndex}`}
                          {...register(`questions.${questionIndex}.${field.name}`, { required: true })}
                          name={field.name}
                          value={question[field.name] || ""}
                          onChange={(e) => {
                          handleChange(e, questionIndex);
                          setValue(`questions.${questionIndex}.${field.name}`, e.target.value);
                        }}
                        className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                        type={field.type}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    )}
                    {(errors?.questions as any)?.[questionIndex]?.[field.name] && (
                      <span className="text-red-500 text-xs italic">
                        {field.label} field is required
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-700">Options:</h3>
                {question.options.map((option: any, optionIndex: number) => (
                  <div key={optionIndex} className="flex items-center gap-4 mb-2">
                    <textarea
                      id={`${questionIndex}-${optionIndex}`}
                      {...register(`questions.${questionIndex}.options.${optionIndex}.optionText`, {
                        required: `Option ${optionIndex + 1} is required.`,
                      })}
                      name="optionText"
                      value={option.optionText || ""}
                      onChange={(e) => {
                        handleChange(e, questionIndex, optionIndex);
                        setValue(
                          `questions.${questionIndex}.options.${optionIndex}.optionText`,
                          e.target.value
                        );
                      }}
                      className="flex-1 p-2 border rounded"
                      placeholder={`Option ${optionIndex + 1}`}
                    />
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        {...register(`questions.${questionIndex}.options.${optionIndex}.isCorrect`, {
                          validate: (value) => {
                            const options = getValues(`questions.${questionIndex}.options`);
                            return options.some((opt: any) => opt.isCorrect) || "At least one option must be correct.";
                          },
                        })}
                        name="isCorrect"
                        checked={option.isCorrect || false}
                        onChange={(e) => {
                          handleChange(e, questionIndex, optionIndex);
                          setValue(
                            `questions.${questionIndex}.options.${optionIndex}.isCorrect`,
                            e.target.checked
                          );
                        }}
                      />
                      Correct
                    </label>
                    {(errors?.questions as any)?.[questionIndex]?.options?.[optionIndex]?.isCorrect && (
                      <span className="text-red-500 text-xs italic">
                        {(errors.questions as any)?.[questionIndex]?.options[optionIndex]?.isCorrect?.message}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {questionIndex !== 0 && (
                <button
                  type="button"
                  onClick={() => deleteQuestion(questionIndex)}
                  className="text-white hover:underline p-2 rounded-full bg-red-500 ml-2"
                  disabled={formData.questions.length <= 1}
                >
                  Delete Question
                </button>
              )}
            </fieldset>
          ))}

          <button
            type="button"
            onClick={addQuestion}
            className="text-white hover:underline p-2 rounded-full bg-blue-500"
          >
            Add Question
          </button>

          <div className="flex justify-start mt-6 ml-10">
            <FormButton className="py-2 px-4 rounded mr-4" type="submit">
              ADD
            </FormButton>
            <CancelButton
              className="py-2 px-4 rounded"
              onClick={handleCancel}
              type="button"
            >
              Cancel
            </CancelButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTestQuestion;